/**
 * Command palette (PLAN.md §5.10) — Ctrl/Cmd-K.
 *
 * Fuzzy search across tickets, companies and contacts, plus a handful of actions. This is the
 * primary navigation for anyone who has used the app for more than a week: typing a ticket
 * number beats three clicks through a board every time.
 *
 * Search is server-side (the client holds one page of results, not the dataset), debounced so a
 * fast typist doesn't fire a request per keystroke, and scoped by the same permissions as the
 * pages it links to — a user without `crm.companies.view` gets no company results because the
 * query is never issued.
 */

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Combobox, Dialog, Transition } from '@headlessui/react';
import {
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  PlusIcon,
  TicketIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { listCompanies, listContacts, listTickets } from '@/services/servicedesk';
import { clsx } from '@/utils/clsx';

/** Long enough that a burst of typing is one request, short enough to feel instant. */
const DEBOUNCE_MS = 200;
const RESULT_LIMIT = 6;

interface Item {
  id: string;
  label: string;
  hint?: string;
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  group: string;
}

export function CommandPalette({
  open,
  onClose,
  onNewTicket,
}: {
  open: boolean;
  onClose: () => void;
  onNewTicket: () => void;
}) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [raw, setRaw] = useState('');
  const [query, setQuery] = useState('');

  // Reset on close so reopening doesn't show the previous search's results.
  useEffect(() => {
    if (!open) {
      setRaw('');
      setQuery('');
    }
  }, [open]);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(raw.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [raw]);

  const canSeeTickets = hasPermission('tickets.view');
  const canSeeCompanies = hasPermission('crm.companies.view');
  const canSeeContacts = hasPermission('crm.contacts.view');
  const enabled = open && query.length >= 2;

  const tickets = useQuery({
    queryKey: ['palette', 'tickets', query],
    queryFn: () => listTickets({ search: query, page_size: RESULT_LIMIT }),
    enabled: enabled && canSeeTickets,
  });
  const companies = useQuery({
    queryKey: ['palette', 'companies', query],
    queryFn: () => listCompanies({ search: query, page_size: RESULT_LIMIT }),
    enabled: enabled && canSeeCompanies,
  });
  const contacts = useQuery({
    queryKey: ['palette', 'contacts', query],
    queryFn: () => listContacts({ search: query, page_size: RESULT_LIMIT }),
    enabled: enabled && canSeeContacts,
  });

  const results = useMemo<Item[]>(() => {
    const items: Item[] = [];
    for (const ticket of tickets.data?.items ?? []) {
      items.push({
        id: ticket.id,
        label: `${ticket.number} — ${ticket.title}`,
        hint: ticket.company_name ?? undefined,
        to: `/tickets/${ticket.id}`,
        icon: TicketIcon,
        group: 'Tickets',
      });
    }
    for (const company of companies.data?.items ?? []) {
      items.push({
        id: company.id,
        label: company.name,
        hint: company.domain ?? undefined,
        to: `/companies/${company.id}`,
        icon: BuildingOffice2Icon,
        group: 'Companies',
      });
    }
    for (const contact of contacts.data?.items ?? []) {
      items.push({
        id: contact.id,
        label: contact.full_name,
        hint: contact.company_name ?? undefined,
        // Contacts have no detail page yet, so land on their company — better than a dead entry.
        to: `/companies/${contact.company_id}`,
        icon: UserIcon,
        group: 'Contacts',
      });
    }
    return items;
  }, [tickets.data, companies.data, contacts.data]);

  const select = useCallback(
    (item: Item | null) => {
      if (!item) return;
      onClose();
      if (item.id === 'action:new-ticket') onNewTicket();
      else navigate(item.to);
    },
    [navigate, onClose, onNewTicket],
  );

  const actions: Item[] = canSeeTickets
    ? [
        {
          id: 'action:new-ticket',
          label: 'New ticket',
          to: '/tickets',
          icon: PlusIcon,
          group: 'Actions',
        },
      ]
    : [];

  const visibleActions = query
    ? actions.filter((a) => a.label.toLowerCase().includes(query.toLowerCase()))
    : actions;

  const grouped = useMemo(() => {
    const map = new Map<string, Item[]>();
    for (const item of [...visibleActions, ...results]) {
      const bucket = map.get(item.group) ?? [];
      bucket.push(item);
      map.set(item.group, bucket);
    }
    return [...map.entries()];
  }, [visibleActions, results]);

  const loading = tickets.isFetching || companies.isFetching || contacts.isFetching;

  return (
    <Transition show={open} as={Fragment} afterLeave={() => setRaw('')}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="transition-opacity duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-surface-900/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4 pt-[12vh]">
          <Transition.Child
            as={Fragment}
            enter="transition duration-200 ease-out"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition duration-150 ease-in"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="mx-auto max-w-xl overflow-hidden rounded-xl bg-white shadow-modal dark:bg-surface-800">
              <Combobox onChange={select}>
                <div className="flex items-center border-b border-surface-200 px-4 dark:border-surface-700">
                  <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-surface-400" aria-hidden="true" />
                  <Combobox.Input
                    autoFocus
                    value={raw}
                    onChange={(e) => setRaw(e.target.value)}
                    placeholder="Search tickets, companies, contacts…"
                    className="w-full border-0 bg-transparent px-3 py-4 text-sm text-surface-900 placeholder:text-surface-400 focus:outline-none focus:ring-0 dark:text-surface-100"
                    displayValue={() => raw}
                  />
                  {loading && (
                    <span className="text-xs text-surface-400" role="status">
                      Searching…
                    </span>
                  )}
                </div>

                <Combobox.Options static className="max-h-80 overflow-y-auto p-2">
                  {grouped.length === 0 ? (
                    <p className="px-3 py-6 text-center text-sm text-surface-500">
                      {query.length < 2 ? 'Type at least two characters.' : 'No matches.'}
                    </p>
                  ) : (
                    grouped.map(([group, items]) => (
                      <div key={group}>
                        <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-surface-500">
                          {group}
                        </p>
                        {items.map((item) => (
                          <Combobox.Option key={`${group}:${item.id}`} value={item} as={Fragment}>
                            {({ active }) => (
                              <li
                                className={clsx(
                                  'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm',
                                  active
                                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300'
                                    : 'text-surface-700 dark:text-surface-200',
                                )}
                              >
                                <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                                {item.hint && (
                                  <span className="shrink-0 text-xs text-surface-400">{item.hint}</span>
                                )}
                              </li>
                            )}
                          </Combobox.Option>
                        ))}
                      </div>
                    ))
                  )}
                </Combobox.Options>
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
